import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Calendar, CircleNotch } from "@phosphor-icons/react";
import { BookingSettingsEditor } from "@/components/therapist/availability/BookingSettingsEditor";
import { DateOverrides } from "@/components/therapist/availability/DateOverrides";
import { WeeklyScheduleEditor } from "@/components/therapist/availability/WeeklyScheduleEditor";
import { TabBar } from "@/components/ui/tab-bar";
import { apiSend } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { validateWeeklySchedule } from "@/lib/availability-segments";
import { useEmergencySettings } from "@/api-queries/emergency";
import type { BookingSettings, DateOverride, TimeBlock } from "@/types";
import { DEFAULT_BOOKING_HORIZON_DAYS } from "@/lib/booking-horizon";
import { SectionShell } from "./section-primitives";

export function AvailabilitySection({
  availability,
  bookingSettings,
  loading,
}: {
  availability?: { schedule: TimeBlock[]; overrides: DateOverride[] };
  bookingSettings?: BookingSettings;
  loading: boolean;
}) {
  const queryClient = useQueryClient();
  const { data: emergencySettings } = useEmergencySettings();
  const emergencyEnabled = emergencySettings?.enabled ?? false;

  // A schedule / override / settings change alters what the calendar and the
  // client booking grid may offer. Invalidate every dependent query, not just
  // the editor's own — otherwise the 60s global staleTime shows stale days.
  const invalidateAvailabilityCaches = () => {
    queryClient.invalidateQueries({ queryKey: ["therapist", "availability"] });
    queryClient.invalidateQueries({ queryKey: ["therapist", "booking-settings"] });
    queryClient.invalidateQueries({ queryKey: ["schedule-overview"] });
    queryClient.invalidateQueries({ queryKey: ["available-slots"] });
  };

  const [schedule, setSchedule] = useState<TimeBlock[]>([]);
  const [settings, setSettings] = useState<BookingSettings>({
    slotDurationMinutes: 50,
    bufferMinutes: 10,
    bookingHorizonDays: DEFAULT_BOOKING_HORIZON_DAYS,
    minBookingLeadDays: 2,
    acceptingNewClients: true,
  });
  const [tab, setTab] = useState<"schedule" | "overrides" | "settings">("schedule");

  useEffect(() => {
    if (availability) setSchedule(availability.schedule.map((b) => ({ ...b, _key: crypto.randomUUID() })));
  }, [availability]);

  useEffect(() => {
    if (bookingSettings) setSettings(bookingSettings);
  }, [bookingSettings]);

  const saveSchedule = useMutation({
    mutationFn: (blocks: TimeBlock[]) =>
      apiSend("/api/therapist/availability", "PUT", blocks, "Greška pri spremanju rasporeda"),
    onSuccess: () => {
      invalidateAvailabilityCaches();
      toast.success("Raspored je sačuvan.");
    },
  });
  const saveSettings = useMutation({
    mutationFn: (data: BookingSettings) =>
      apiSend("/api/therapist/booking-settings", "PATCH", data, "Greška pri spremanju pravila"),
    onSuccess: () => {
      invalidateAvailabilityCaches();
      toast.success("Pravila rezervacija su sačuvana.");
    },
  });
  const addOverride = useMutation({
    mutationFn: (data: { date: string; type: "add" | "block"; startTime?: string; endTime?: string }) =>
      apiSend("/api/therapist/availability/overrides", "POST", data, "Greška pri dodavanju izuzetka"),
    onSuccess: () => invalidateAvailabilityCaches(),
  });
  const addRangeOverride = useMutation({
    mutationFn: (data: { startDate: string; endDate: string }) =>
      apiSend("/api/therapist/availability/overrides/range", "POST", data, "Greška pri dodavanju izuzetka"),
    onSuccess: () => invalidateAvailabilityCaches(),
  });
  const deleteOverride = useMutation({
    mutationFn: (id: string) =>
      apiSend(`/api/therapist/availability/overrides/${id}`, "DELETE", undefined, "Greška pri brisanju izuzetka"),
    onSuccess: () => invalidateAvailabilityCaches(),
  });

  if (loading) {
    return <CircleNotch className="mx-auto mt-20 size-8 animate-spin text-primary" />;
  }

  return (
    <SectionShell
      icon={Calendar}
      title="Radno vrijeme i pravila rezervacija"
      description="Kalendar za sesije ostaje operativni ekran; ovdje podešavate kada i kako klijenti mogu rezervisati."
    >
      <div className="mb-5 inline-flex max-w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <TabBar
          tabs={[
            { value: "schedule", label: "Sedmični raspored" },
            { value: "overrides", label: "Izuzeci" },
            { value: "settings", label: "Pravila rezervacija" },
          ]}
          value={tab}
          onChange={(v) => setTab(v as typeof tab)}
        />
      </div>
      {tab === "schedule" && (
        <WeeklyScheduleEditor
          schedule={schedule}
          setSchedule={setSchedule}
          onSave={(blocks) => {
            // Isto pravilo kao onboarding i server: po danu početak prije kraja, bez preklapanja.
            const error = validateWeeklySchedule(blocks);
            if (error) {
              toast.error(error);
              return;
            }
            saveSchedule.mutate(blocks);
          }}
          isSaving={saveSchedule.isPending}
          emergencyEnabled={emergencyEnabled}
        />
      )}
      {tab === "overrides" && (
        <DateOverrides
          overrides={availability?.overrides ?? []}
          onAdd={(data) => addOverride.mutate(data)}
          onAddRange={(data) => addRangeOverride.mutate(data)}
          onDelete={(id) => deleteOverride.mutate(id)}
          isAdding={addOverride.isPending}
          isAddingRange={addRangeOverride.isPending}
          isDeleting={deleteOverride.isPending}
        />
      )}
      {tab === "settings" && (
        <BookingSettingsEditor
          settings={settings}
          setSettings={setSettings}
          onSave={(data) => saveSettings.mutate(data)}
          isSaving={saveSettings.isPending}
        />
      )}
    </SectionShell>
  );
}
